/**
 * Modal para crear/editar categoría
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 5.3
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import axios from 'axios';
import { ErpContext } from '../../../context/ErpContext';
import { validarCategoria } from '../../../utils/soporte/soporteValidations';

const ModalCrearCategoria = ({ open, onClose, categoria }) => {
  const { serverURL } = useContext(ErpContext);
  const esEdicion = !!categoria;
  
  const [formData, setFormData] = useState({
    nombre: '',
    modulo: '',
    descripcion: '',
    orden: 0,
    activa: true
  });
  
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Resetear formulario cuando cambia la categoría o se abre/cierra el modal
  useEffect(() => {
    if (open) {
      if (categoria) {
        // Modo edición: cargar datos de la categoría
        setFormData({
          nombre: categoria.nombre || '',
          modulo: categoria.modulo || '',
          descripcion: categoria.descripcion || '',
          orden: categoria.orden || 0,
          activa: categoria.activa !== undefined ? categoria.activa : true
        });
      } else {
        // Modo creación: resetear formulario
        setFormData({
          nombre: '',
          modulo: '',
          descripcion: '',
          orden: 0,
          activa: true
        });
      }
      // Limpiar errores
      setErrors({});
      setError('');
    }
  }, [categoria, open]);
  
  /**
   * Manejar cambio en campos del formulario
   */
  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  /**
   * Validar formulario usando soporteValidations
   */
  const validar = () => {
    const validationErrors = validarCategoria(formData);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };
  
  /**
   * Manejar envío del formulario
   */
  const handleSubmit = async () => {
    // Validar antes de enviar
    if (!validar()) {
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      if (esEdicion) {
        // Editar categoría existente
        await axios.patch(
          `${serverURL}/soporteAPI/categorias/${categoria.id}`,
          formData,
          { withCredentials: true }
        );
      } else {
        // Crear nueva categoría
        await axios.post(
          `${serverURL}/soporteAPI/categorias`,
          formData,
          { withCredentials: true }
        );
      }
      
      // Cerrar modal y recargar lista (pasar true para indicar que se debe recargar)
      onClose(true);
    } catch (err) {
      console.error('Error guardando categoría:', err);
      setError(err.response?.data?.error || 'Error al guardar la categoría');
    } finally {
      setSaving(false);
    }
  };
  
  /**
   * Manejar cierre del modal
   */
  const handleClose = () => {
    if (!saving) {
      onClose(false); // false = no recargar
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={saving}
    >
      <DialogTitle>
        {esEdicion ? 'Editar Categoría' : 'Nueva Categoría'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Nombre */}
          <TextField
            fullWidth
            required
            label="Nombre"
            value={formData.nombre}
            onChange={handleChange('nombre')}
            error={!!errors.nombre}
            helperText={errors.nombre}
            disabled={saving}
            autoFocus
          />
          
          {/* Módulo */}
          <TextField
            fullWidth
            required
            label="Módulo"
            value={formData.modulo}
            onChange={handleChange('modulo')}
            error={!!errors.modulo}
            helperText={errors.modulo || 'Ej: ventas, compras, inventario'}
            disabled={saving}
            placeholder="ventas"
          />
          
          {/* Descripción */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Descripción"
            value={formData.descripcion}
            onChange={handleChange('descripcion')}
            error={!!errors.descripcion}
            helperText={errors.descripcion}
            disabled={saving}
            placeholder="Descripción de la categoría..."
          />
          
          {/* Orden */}
          <TextField
            fullWidth
            type="number"
            label="Orden"
            value={formData.orden}
            onChange={handleChange('orden')}
            error={!!errors.orden}
            helperText={errors.orden || 'Orden de visualización (menor número = aparece primero)'}
            disabled={saving}
            inputProps={{ min: 0 }}
          />
          
          {/* Activa */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.activa}
                onChange={handleChange('activa')}
                disabled={saving}
              />
            }
            label="Categoría activa"
          />
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={saving}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Guardando...' : esEdicion ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalCrearCategoria;

